import React, { useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import DraggableDialogPaper from "./DraggableDialogPaper";

function getRoomLabel(room) {
  const bits = [
    room.campus,
    room.house,
    room.roomName || room.bedroomName || room.groupName,
  ].filter(Boolean);

  return bits.join(" · ");
}

function CopyRoomScheduleDialog({
  open,
  onClose,
  rooms = [],
  onSave,
}) {
  const [sourceRoomId, setSourceRoomId] = useState("");
  const [targetRoomIds, setTargetRoomIds] = useState([]);

  const sourceRoom = useMemo(
    () => rooms.find((room) => String(room.id) === String(sourceRoomId)) || null,
    [rooms, sourceRoomId]
  );

  const availableTargets = useMemo(() => {
    return rooms.filter((room) => String(room.id) !== String(sourceRoomId));
  }, [rooms, sourceRoomId]);

  const canSave =
    sourceRoomId &&
    Array.isArray(targetRoomIds) &&
    targetRoomIds.length > 0;

  const handleClose = () => {
    setSourceRoomId("");
    setTargetRoomIds([]);
    onClose();
  };

  const handleSave = async () => {
    await onSave({
      sourceRoomId,
      targetRoomIds,
    });

    setSourceRoomId("");
    setTargetRoomIds([]);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperComponent={DraggableDialogPaper}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{ cursor: "move", userSelect: "none" }}
      >
        Copy Room Schedule
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Source Room</InputLabel>
            <Select
              value={sourceRoomId}
              label="Source Room"
              onChange={(e) => {
                setSourceRoomId(e.target.value);
                setTargetRoomIds([]);
              }}
            >
              {rooms.map((room) => (
                <MenuItem key={room.id} value={room.id}>
                  {getRoomLabel(room)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={!sourceRoomId}>
            <InputLabel>Target Rooms</InputLabel>
            <Select
              multiple
              value={targetRoomIds}
              label="Target Rooms"
              onChange={(e) => setTargetRoomIds(e.target.value)}
              renderValue={(selected) => `${selected.length} room${selected.length === 1 ? "" : "s"} selected`}
            >
              {availableTargets.map((room) => (
                <MenuItem key={room.id} value={room.id}>
                  <Checkbox checked={targetRoomIds.includes(room.id)} />
                  <ListItemText primary={getRoomLabel(room)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {sourceRoom && (
            <Typography variant="body2" color="text.secondary">
              Source schedule: {sourceRoom.scheduledDayCount ?? 0} day
              {(sourceRoom.scheduledDayCount ?? 0) === 1 ? "" : "s"}
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          Copy Schedule
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CopyRoomScheduleDialog;
